import React, { useEffect, useState, useContext } from "react";
import { Card, Button, Tag, Spin, Row, Col, Typography, Modal, message, Empty, Input, Space, Layout, Grid, Segmented } from "antd";
import {
  CreditCardOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  AppstoreOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../Constants";
import AppContext from "../../AppContext";

const { Title, Text } = Typography;
const { Content } = Layout;

// Entitlement status -> display label/color. Renders the entitlement's real status
// ("active" -> ACTIVE) instead of the ambiguous "AVAILABLE", without touching the
// backend's status values themselves.
const STATUS_KEYS = { active: "active", consumed: "consumed", expired: "expired" };
const STATUS_COLORS = { active: "green", consumed: "default", expired: "red" };

// Shared label map for the boolean feature flags returned on both entitlements
// (flat, e.g. item.hasQrCheckIn) and catalog packages (nested under pkg.features).
// Only flags present and true are ever shown — nothing here is fabricated.
const FEATURE_KEYS = [
  "hasQrCheckIn",
  "customizationOptions",
  "hasWhatsAppSupport",
  "hasSmsSupport",
  "hasDigitalPassSupport",
  "hasLiveDashboard",
  "hasPostEventAnalytics",
  "hasVipSupport",
  "hasSpeakerSupport",
  "hasExhibitorSupport",
  "hasExportSupport",
];

// Entitlements carry their feature flags flat on the object.
const getEntitlementFeatures = (item) => FEATURE_KEYS.filter((key) => item[key]);

// Catalog packages carry theirs nested under `features`.
const getPackageFeatures = (pkg) => FEATURE_KEYS.filter((key) => pkg.features?.[key]);

const ProductTypeTag = ({ productType, style }) => {
  const { t } = useTranslation();
  return productType === "wedding" ? (
    <Tag color="#eb2f96" icon={<TeamOutlined />} style={{ borderRadius: 4, fontWeight: "bold", ...style }}>
      {t("myPackages.productType.wedding")}
    </Tag>
  ) : (
    <Tag color="blue" icon={<AppstoreOutlined />} style={{ borderRadius: 4, ...style }}>
      {t("myPackages.productType.event")}
    </Tag>
  );
};

// On phones only the first few features show, with a "+N more" toggle, so stacked package
// cards stay short.
const MOBILE_FEATURE_LIMIT = 4;

const FeatureList = ({ features }) => {
  const { t } = useTranslation();
  const isMobile = Grid.useBreakpoint().md === false;
  const [expanded, setExpanded] = useState(false);
  if (features.length === 0) return null;
  const collapsible = isMobile && features.length > MOBILE_FEATURE_LIMIT;
  const visible = collapsible && !expanded ? features.slice(0, MOBILE_FEATURE_LIMIT) : features;
  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #f0f0f0" }}>
      <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {t("myPackages.includedFeatures")}
      </Text>
      <div
        style={{
          marginTop: 10,
          display: "grid",
          // auto-fit responds to the card's real rendered width (a true container query),
          // so it naturally collapses to one column on a narrow card without a media query.
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          rowGap: 8,
          columnGap: 12,
        }}
      >
        {visible.map((key) => (
          <div
            key={key}
            style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, lineHeight: 1.4, color: "#434343" }}
          >
            <CheckCircleFilled style={{ color: "#52c41a", flexShrink: 0 }} />
            <span>{t(`myPackages.features.${key}`)}</span>
          </div>
        ))}
      </div>
      {collapsible && (
        <Button type="link" size="small" style={{ padding: 0, marginTop: 6 }} onClick={() => setExpanded((v) => !v)}>
          {expanded
            ? t("myPackages.showLess")
            : t("myPackages.moreFeatures", { count: features.length - MOBILE_FEATURE_LIMIT })}
        </Button>
      )}
    </div>
  );
};

// Fixed-width auto-fill grid (rather than antd's fixed-column List grid) so a single
// card stays a comfortable, non-stretched width instead of shrinking to a fraction of
// a fixed column count or ballooning to fill an unrelated container.
const cardGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(min(320px, 100%), 400px))",
  gap: 16,
};

export default function MyPackagesPage() {
  const { t } = useTranslation();
  const authCtx = useContext(AppContext);
  // `=== false` so the first render (before antd reports breakpoints) defaults to desktop.
  const isMobile = Grid.useBreakpoint().md === false;
  const [entitlements, setEntitlements] = useState([]);
  const [catalog, setCatalog] = useState([]);
  // Filter in the "Choose a Package" window: all, Social Gatherings (wedding) or Events.
  const [packageFilter, setPackageFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [purchaseModalVisible, setPurchaseModalVisible] = useState(false);
  const [submittingPackageId, setSubmittingPackageId] = useState(null);
  // Keyed by package id: { code, validating, appliedCoupon, message }. appliedCoupon holds
  // the coupon DTO returned by /Coupon/validate, used only to show an estimated discount —
  // the server independently recalculates and enforces the real discount at payment time.
  const [couponState, setCouponState] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [entitlementsRes, catalogRes] = await Promise.all([
        axios.get(`${BASE_URL}/Company/my-entitlements`),
        axios.get(`${BASE_URL}/EventProductPackage/active`),
      ]);
      setEntitlements(entitlementsRes.data || []);
      setCatalog(catalogRes.data || []);
    } catch (err) {
      console.error("Failed to load packages page data", err);
      message.error(t("myPackages.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleCouponCodeChange = (pkgId, code) => {
    setCouponState((prev) => ({ ...prev, [pkgId]: { ...prev[pkgId], code, appliedCoupon: null, message: null } }));
  };

  const handleApplyCoupon = async (pkg) => {
    const code = couponState[pkg.id]?.code?.trim();
    if (!code) return;
    setCouponState((prev) => ({ ...prev, [pkg.id]: { ...prev[pkg.id], validating: true, message: null } }));
    try {
      const response = await axios.post(`${BASE_URL}/Coupon/validate`, {
        code,
        productType: pkg.productType,
        eventPackageSku: pkg.sku,
        email: authCtx.profile?.email || "",
      });
      const result = response.data;
      if (result?.isValid) {
        setCouponState((prev) => ({
          ...prev,
          [pkg.id]: { ...prev[pkg.id], validating: false, appliedCoupon: result.coupon, message: null },
        }));
        message.success(t("myPackages.coupon.appliedSuccess"));
      } else {
        setCouponState((prev) => ({
          ...prev,
          [pkg.id]: { ...prev[pkg.id], validating: false, appliedCoupon: null, message: result?.message || t("myPackages.coupon.invalid") },
        }));
      }
    } catch (err) {
      console.error("Coupon validation error", err);
      setCouponState((prev) => ({
        ...prev,
        [pkg.id]: { ...prev[pkg.id], validating: false, appliedCoupon: null, message: t("myPackages.coupon.failedValidate") },
      }));
    }
  };

  // Display-only estimate; the server independently recalculates the real discount.
  const getEstimatedPrice = (pkg) => {
    // Backend is authoritative — this is a pre-payment estimate only (same convention as
    // FutureCardsWebsite/signup.jsx's coupon reflection).
    const coupon = couponState[pkg.id]?.appliedCoupon;
    const couponType = coupon ? String(coupon.type || "").toLowerCase() : null;
    const discountType = coupon ? String(coupon.discountType || "").toLowerCase() : null;
    const discountValue = coupon ? Number(coupon.discountValue) : 0;
    if (!coupon || couponType !== "discount" || !discountType || !(discountValue > 0)) {
      return pkg.price;
    }
    return discountType === "percentage"
      ? Math.max(0, pkg.price * (1 - discountValue / 100))
      : Math.max(0, pkg.price - discountValue);
  };

  const renderCouponInput = (pkg) => {
    const state = couponState[pkg.id] || {};
    return (
      <Space.Compact style={{ width: "100%", marginTop: 8 }}>
        <Input
          placeholder={t("myPackages.coupon.placeholder")}
          value={state.code || ""}
          disabled={!!state.appliedCoupon}
          onChange={(e) => handleCouponCodeChange(pkg.id, e.target.value)}
        />
        <Button loading={state.validating} disabled={!!state.appliedCoupon} onClick={() => handleApplyCoupon(pkg)}>
          {t("myPackages.coupon.apply")}
        </Button>
      </Space.Compact>
    );
  };

  const handlePurchase = async (pkg) => {
    setSubmittingPackageId(pkg.id);
    try {
      const paymentData = {
        employeeId: authCtx.profile.id,
        fullName: `${authCtx.profile.firstName || ""} ${authCtx.profile.lastName || ""}`.trim(),
        email: authCtx.profile.email || "",
        phone: "",
        planAmount: pkg.price,
        finalAmount: pkg.price,
        planId: "",
        discountCode: couponState[pkg.id]?.appliedCoupon ? couponState[pkg.id].code : null,
        productPackageId: pkg.id,
        productType: pkg.productType,
      };

      // Uses the same PaymentInitiationResult contract as the rest of the app (see
      // SubscriptionDetails.js): success + requiresPayment decide whether to follow
      // paymentUrl (ClickPay) or redirectUrl (completed server-side, no charge).
      const response = await axios.post(`${BASE_URL}/Payment/initiate`, paymentData);
      if (response.data?.success) {
        const target =
          response.data.requiresPayment === false
            ? response.data.redirectUrl
            : response.data.paymentUrl;
        if (target) {
          window.location.href = target;
          return;
        }
      }
      message.error(t("myPackages.errors.initiateFailed"));
    } catch (err) {
      console.error("Purchase error", err);
      message.error(t("myPackages.errors.redirectError"));
    } finally {
      setSubmittingPackageId(null);
    }
  };

  const pendingEntitlements = entitlements.filter((e) => e.status === "pending_payment");
  // Active packages first, then consumed/expired ones.
  const activeEntitlements = entitlements
    .filter((e) => e.status !== "pending_payment")
    .sort((a, b) => (b.status === "active") - (a.status === "active"));

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Layout className="min-h-full bg-transparent">
      <header className="py-4 bg-transparent">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">{t("myPackages.title")}</h1>
        </div>
      </header>
      <Content className="rounded-lg px-3 py-4 shadow bg-white sm:px-6 sm:py-6">
      <Row justify="space-between" align="middle" style={{ marginBottom: isMobile ? "16px" : "24px" }}>
        {!isMobile && (
          <Col>
            <Text type="secondary">{t("myPackages.subtitle")}</Text>
          </Col>
        )}
        <Col xs={24} md="auto">
          <Button
            type="primary"
            icon={<ShoppingCartOutlined />}
            size="large"
            block={isMobile}
            onClick={() => setPurchaseModalVisible(true)}
          >
            {t("myPackages.buyAnotherPackage")}
          </Button>
        </Col>
      </Row>

      {pendingEntitlements.map((item) => {
        const matchingCatalog = catalog.find((c) => c.sku === item.sku);
        if (!matchingCatalog) return null;
        return (
          <Card
            key={item.id}
            style={{ marginBottom: "24px", border: "1px solid #ffe58f", background: "#fffbe6" }}
            title={<Text strong style={{ color: "#d48806" }}>{t("myPackages.pendingCard.title")}</Text>}
          >
            <Row justify="space-between" align="middle" gutter={[0, 12]}>
              <Col flex="auto">
                <Title level={4}>{matchingCatalog.nameEn}</Title>
                <Text>
                  {t("myPackages.pendingCard.priceLabel")} {getEstimatedPrice(matchingCatalog)} {matchingCatalog.currency}
                  {couponState[matchingCatalog.id]?.appliedCoupon && (
                    <Text delete type="secondary" style={{ marginInlineStart: 8 }}>
                      {matchingCatalog.price} {matchingCatalog.currency}
                    </Text>
                  )}
                  {" "}{t("myPackages.pendingCard.includesLimit", { count: matchingCatalog.productType === "wedding" ? matchingCatalog.maxInvitations : matchingCatalog.maxRegistrations })}
                </Text>
                <div style={{ maxWidth: 320 }}>{renderCouponInput(matchingCatalog)}</div>
                {couponState[matchingCatalog.id]?.message && (
                  <Text type="danger">{couponState[matchingCatalog.id].message}</Text>
                )}
              </Col>
              <Col xs={24} md="auto">
                <Button
                  type="primary"
                  danger
                  block={isMobile}
                  icon={<CreditCardOutlined />}
                  loading={submittingPackageId === matchingCatalog.id}
                  onClick={() => handlePurchase(matchingCatalog)}
                >
                  {t("myPackages.pendingCard.payAndActivate")}
                </Button>
              </Col>
            </Row>
          </Card>
        );
      })}

      <div style={{ marginTop: pendingEntitlements.length ? 8 : 0 }}>
        <Title level={4} style={{ marginBottom: 16 }}>
          {t("myPackages.purchasedPackagesTitle")}
        </Title>
        {activeEntitlements.length === 0 ? (
          <Empty description={t("myPackages.emptyDescription")} />
        ) : (
          <div style={cardGridStyle}>
            {activeEntitlements.map((item) => {
              const matchingCatalog = catalog.find((c) => c.sku === item.sku);
              const usageLabel = item.productType === "wedding" ? t("myPackages.usage.invitations") : t("myPackages.usage.registrations");
              const unitLabel = item.productType === "wedding" ? t("myPackages.unit.guests") : t("myPackages.unit.registrations");
              return (
                <Card key={item.id} style={{ borderRadius: 12 }} styles={{ body: { padding: isMobile ? 16 : 20 } }}>
                  <Row justify="space-between" align="top" wrap={false}>
                    <Text strong style={{ fontSize: 16 }}>
                      {matchingCatalog ? matchingCatalog.nameEn : item.sku}
                    </Text>
                    <Tag color={STATUS_COLORS[item.status] || "orange"} style={{ marginInlineEnd: 0 }}>
                      {STATUS_KEYS[item.status] ? t(`myPackages.status.${STATUS_KEYS[item.status]}`) : item.status?.toUpperCase()}
                    </Tag>
                  </Row>
                  <ProductTypeTag productType={item.productType} style={{ marginTop: 8 }} />

                  <div style={{ marginTop: 16 }}>
                    <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      {usageLabel}
                    </Text>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#1f1f1f", lineHeight: 1.4 }}>
                      {item.productType === "wedding" ? item.maxInvitations : (item.maxRegistrations ?? item.maxInvitations)}{" "}
                      <span style={{ fontSize: 14, fontWeight: 400, color: "#8c8c8c" }}>{unitLabel}</span>
                    </div>
                  </div>

                  <FeatureList features={getEntitlementFeatures(item)} />
                </Card>
              );
            })}
          </div>
        )}
      </div>
      </Content>

      <Modal
        title={t("myPackages.modal.title")}
        open={purchaseModalVisible}
        onCancel={() => setPurchaseModalVisible(false)}
        footer={null}
        width={880}
        style={{ maxWidth: "95vw" }}
      >
        <Segmented
          block
          value={packageFilter}
          onChange={setPackageFilter}
          options={[
            { value: "all", label: t("myPackages.modal.filterAll") },
            { value: "wedding", label: t("myPackages.productType.wedding") },
            { value: "event", label: t("myPackages.productType.event") },
          ]}
          style={{ marginTop: 8 }}
        />
        {catalog.filter((pkg) => packageFilter === "all" || pkg.productType === packageFilter).length === 0 && (
          <Empty description={t("myPackages.modal.noPackagesInFilter")} style={{ marginTop: 16 }} />
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 16,
            marginTop: 16,
          }}
        >
          {catalog.filter((pkg) => packageFilter === "all" || pkg.productType === packageFilter).map((pkg) => {
            const state = couponState[pkg.id] || {};
            const usageLabel = pkg.productType === "wedding" ? t("myPackages.usage.invitations") : t("myPackages.usage.registrations");
            const unitLabel = pkg.productType === "wedding" ? t("myPackages.unit.guests") : t("myPackages.unit.registrations");
            return (
              <Card
                key={pkg.id}
                style={{ borderRadius: 12, display: "flex", flexDirection: "column" }}
                styles={{ body: { padding: 20, display: "flex", flexDirection: "column", flex: 1 } }}
              >
                <div>
                  <Text strong style={{ fontSize: 16 }}>
                    {pkg.nameEn}
                  </Text>
                  <ProductTypeTag productType={pkg.productType} style={{ marginTop: 8 }} />
                  <div style={{ marginTop: 4, display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                    <Text strong style={{ fontSize: 22, color: "#3f8600" }}>
                      {getEstimatedPrice(pkg)} {pkg.currency}
                    </Text>
                    {/* Only cross out the price when the coupon actually changed it. A trial
                        coupon (type "coupon") waives the charge instead of discounting the
                        price, so the estimate above stays at pkg.price - showing a strikethrough
                        of the same number next to it would read as "discount applied, nothing changed". */}
                    {state.appliedCoupon && getEstimatedPrice(pkg) < pkg.price && (
                      <Text delete type="secondary" style={{ fontSize: 13 }}>
                        {pkg.price} {pkg.currency}
                      </Text>
                    )}
                  </div>
                  {/* Trial coupons scoped to an event/wedding package waive the charge outright
                      (trialDurationDays is null for these - see CouponForm.jsx) rather than
                      granting N free days, so this just says the package itself is free. */}
                  {state.appliedCoupon && String(state.appliedCoupon.type || "").toLowerCase() === "coupon" && (
                    <Text type="success" style={{ fontSize: 12, display: "block", marginTop: 2 }}>
                      {t("myPackages.coupon.freePackage")}
                    </Text>
                  )}
                </div>

                <div style={{ marginTop: 16 }}>
                  <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
                    {usageLabel}
                  </Text>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#1f1f1f", lineHeight: 1.4 }}>
                    {pkg.productType === "wedding" ? pkg.maxInvitations : pkg.maxRegistrations}{" "}
                    <span style={{ fontSize: 13, fontWeight: 400, color: "#8c8c8c" }}>{unitLabel}</span>
                  </div>
                </div>

                <FeatureList features={getPackageFeatures(pkg)} />

                <div style={{ marginTop: "auto", paddingTop: 16 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t("myPackages.coupon.label")}
                  </Text>
                  {renderCouponInput(pkg)}
                  {state.message && (
                    <Text type="danger" style={{ display: "block", marginTop: 4, fontSize: 12 }}>
                      {state.message}
                    </Text>
                  )}
                  <Button
                    type="primary"
                    block
                    size="large"
                    icon={<CreditCardOutlined />}
                    loading={submittingPackageId === pkg.id}
                    onClick={() => handlePurchase(pkg)}
                    style={{ marginTop: 12 }}
                  >
                    {t("myPackages.modal.buyNow")}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </Modal>
    </Layout>
  );
}
